import { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from '@mui/material'
import { styled } from '@mui/material/styles'
import { SearchItemLabel, usePopup } from '..'
import { Checkbox } from '../../../inputs/Checkbox'
import { Text } from '../../../typography'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { useWbsItemRepository } from '../../../../../services/adaptors/wbsItemRepositoryAdaptor'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'
import { SelectOption } from '../../../../model/selectOption'

const PopoverContent = styled('div')({
  padding: '8px',
})

const OptionRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const OptionLabel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  margin: '2px 10px',
})

const OptionPath = styled('span')({
  fontSize: '11px',
  lineHeight: '14px',
  color: colorPalette.monotone[3],
})

type ProcessScopeConditionItemLabelProps = {
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
  onClose: () => void
  search: () => void
  isLoading: boolean
}

export const ProcessScopeConditionItemLabel = ({
  value,
  onChange,
  onClose,
  search,
  isLoading,
}: ProcessScopeConditionItemLabelProps) => {
  const { ref, open, openPopup, closePopup } = usePopup()
  const { projectUuid } = useProjectPrivateContext()
  const { fetchListAsReferencedEntity } = useWbsItemRepository()
  const [options, setOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (!projectUuid) return
    fetchListAsReferencedEntity(projectUuid, [
      WbsItemType.PROCESS,
      WbsItemType.DELIVERABLE_LIST,
    ]).then(results =>
      setOptions(
        results.map(item => ({
          value: item.uuid,
          name: item.name,
          iconUrl: item.iconUrl,
          tooltip: item.path,
        }))
      )
    )
  }, [projectUuid, fetchListAsReferencedEntity])

  const selectedOptions = useMemo(() => {
    return options.filter(op => (value || []).includes(op.value))
  }, [value, options])

  const valueNames = useMemo(() => {
    return selectedOptions.map(op => op.name)
  }, [selectedOptions])

  const onClear = useCallback(() => {
    onChange(undefined)
    onClose()
  }, [onChange, onClose])

  const onClosePopup = useCallback(() => {
    search()
    closePopup()
  }, [search, closePopup])

  const onChangeValue = useCallback(
    (v: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = [...(value || [])]
      const targetValue = v.target.defaultValue
      if (newValue.includes(targetValue)) {
        newValue.splice(newValue.indexOf(targetValue), 1)
      } else {
        newValue.push(targetValue)
      }
      onChange(newValue.length > 0 ? newValue : undefined)
      if (newValue.length === 0) onClose()
    },
    [value, onChange, onClose]
  )

  if (selectedOptions.length === 0) return <></>
  return (
    <>
      <SearchItemLabel
        ref={ref}
        active={open}
        onClick={openPopup}
        onClear={onClear}
        isLoading={isLoading}
      >
        {intl.formatMessage({ id: 'wbsItemSearchFilterPanel.processScope' })}:{' '}
        {valueNames.join(', ')}
      </SearchItemLabel>
      <Popover
        open={open && !isLoading}
        anchorEl={ref.current || null}
        onClose={onClosePopup}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <PopoverContent>
          {options.map(option => (
            <OptionRow key={option.value}>
              <Checkbox
                sx={{ marginRight: '8px' }}
                value={option.value}
                checked={(value || []).some(v => v === option.value)}
                onChange={onChangeValue}
              />
              {option.iconUrl && <img src={option.iconUrl} />}
              <OptionLabel>
                {option.tooltip && <OptionPath>{option.tooltip}</OptionPath>}
                <Text color={colorPalette.monotone[4]}>{option.name}</Text>
              </OptionLabel>
            </OptionRow>
          ))}
        </PopoverContent>
      </Popover>
    </>
  )
}
